/* ============================================================
   Custom Mega Menu — shared behavior
   File: assets/custom-mega-menu.js
   ============================================================ */
(function () {
  if (customElements.get('custom-mega-menu')) return;

  class CustomMegaMenu extends HTMLElement {
    connectedCallback() {
      this.removeAttribute('hidden');
      this._setup();
    }

    _setup() {
      if (this._inited) return;
      const trigger = document.querySelector(this.dataset.trigger);
      if (!trigger) {
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this._setup(), { once: true });
        }
        return;
      }
      this._inited = true;
      this._trigger = trigger;
      this._openOn = this.dataset.openOn || 'hover';
      // Mark click-mode triggers and inject a chevron element so CSS can flip it
      if (this._openOn === 'click') {
        this._trigger.classList.add('cmm-click-trigger');
        if (!this._trigger.querySelector('.cmm-chevron')) {
          const chev = document.createElement('span');
          chev.className = 'cmm-chevron';
          chev.setAttribute('aria-hidden', 'true');
          this._trigger.appendChild(chev);
        }
      }
      this._bind();
      this._wireTabs();
      this._position();
      window.addEventListener('resize', () => this._position());
    }

    _bind() {
      const open = () => this._open();
      const close = () => this._close();
      if (this._openOn === 'hover') {
        this._trigger.addEventListener('mouseenter', open);
        this._trigger.addEventListener('focus', open, true);
        this._trigger.addEventListener('mouseleave', () => this._scheduleClose());
        this.addEventListener('mouseenter', () => this._cancelClose());
        this.addEventListener('mouseleave', close);
      } else {
        this._trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.classList.contains('is-open') ? close() : open();
        });
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target) && !this._trigger.contains(e.target)) close();
        });
      }
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    }

    _wireTabs() {
      // Click-to-switch (no hover) — keeps keyboard focus working for accessibility.
      // Skip rail items rendered as direct links (e.g. "All Accessories") so they navigate normally.
      this.addEventListener('click', (e) => {
        const btn = e.target.closest && e.target.closest('.cmm__rail-btn');
        if (btn && this.contains(btn) && !btn.classList.contains('cmm__rail-btn--link')) {
          e.preventDefault();
          this._activate(btn);
        }
      });
      this.addEventListener('focusin', (e) => {
        const btn = e.target.closest && e.target.closest('.cmm__rail-btn');
        if (btn && this.contains(btn) && !btn.classList.contains('cmm__rail-btn--link')) {
          this._activate(btn);
        }
      });
    }

    _activate(btn) {
      const btns = this.querySelectorAll('.cmm__rail-btn');
      const panes = this.querySelectorAll('.cmm__pane');
      btns.forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
      panes.forEach((p) => p.classList.remove('is-active'));
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      const target = this.querySelector('#' + btn.dataset.target);
      if (target) target.classList.add('is-active');
    }

    _position() {
      if (!this._trigger) return;
      const r = this._trigger.getBoundingClientRect();
      this.style.top = (r.bottom + window.scrollY) + 'px';
    }

    _open() { this._cancelClose(); this._position(); this.classList.add('is-open'); this._trigger.setAttribute('aria-expanded', 'true'); }
    _close() { this.classList.remove('is-open'); this._trigger.setAttribute('aria-expanded', 'false'); }
    _scheduleClose() { this._cancelClose(); this._t = setTimeout(() => this._close(), 180); }
    _cancelClose() { if (this._t) clearTimeout(this._t); }
  }

  customElements.define('custom-mega-menu', CustomMegaMenu);
})();

/* ============================================================
   Filter sidebar: inject a "Clear All Filters" button at the top
   when any filter is active in the URL.
   Runs on collection and search pages.
   ============================================================ */
(function () {
  function hasActiveFilters() {
    const sp = new URLSearchParams(location.search);
    for (const key of sp.keys()) {
      if (key.startsWith('filter.')) return true;
    }
    return false;
  }

  function clearAllUrl() {
    const url = new URL(location.href);
    Array.from(url.searchParams.keys()).forEach((k) => {
      if (k.startsWith('filter.')) url.searchParams.delete(k);
    });
    return url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '');
  }

  function inject() {
    if (!hasActiveFilters()) return;
    // Find the filter form root — try common Halo selectors
    const drawer = document.querySelector('hdt-drawer.hdt-filter');
    if (!drawer) return;
    // Insert before the first filter group
    const firstGroup = drawer.querySelector('.hdt-filter-group, hdt-facet-filters-form, #hdt-facet-filters-form');
    if (!firstGroup) return;
    if (drawer.querySelector('.cmm-clear-all-filters')) return; // already injected

    const a = document.createElement('a');
    a.className = 'cmm-clear-all-filters';
    a.href = clearAllUrl();
    a.innerHTML = '<span>Clear All Filters</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    firstGroup.parentElement.insertBefore(a, firstGroup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', inject);
  } else {
    inject();
  }
  // Re-inject after Halo's filter form re-renders (e.g. after a filter change)
  const obs = new MutationObserver(() => inject());
  obs.observe(document.body, { childList: true, subtree: true });
})();


/* ============================================================
   Top bar: inject Visit Us | Contact Us links left of socials
   ============================================================ */
(function () {
  function injectTopBarLinks() {
    const socials = document.querySelector('.hdt-top-bar__item.hdt-top-bar__socials');
    if (!socials) return;
    if (socials.querySelector('.cmm-topbar-links')) return;
    const ul = socials.querySelector('.hdt-socials__list, ul');
    const wrap = document.createElement('div');
    wrap.className = 'cmm-topbar-links';
    wrap.innerHTML = '<a href="https://drive.google.com/drive/folders/1XwHiEdOUnfcnVQ3BrMcSoqA894vAa3Ev" target="_blank" rel="noopener">Download / Manuals</a><span class="cmm-topbar-divider" aria-hidden="true">|</span><a href="/pages/contact">Contact Us</a>';
    if (ul) {
      socials.insertBefore(wrap, ul);
    } else {
      socials.insertBefore(wrap, socials.firstChild);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectTopBarLinks);
  } else {
    injectTopBarLinks();
  }
})();


/* ============================================================
   Filter sidebar: collapsible filter sections
   Click a filter group's summary header to collapse/expand it.
   Bypasses Halo's <hdt-accordion> which prevents native <details> toggle.
   State persists via localStorage so user choices survive navigation.
   ============================================================ */
(function () {
  var STORAGE_KEY = 'cmm:filterCollapse';

  function getState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function setState(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    catch (e) {}
  }
  function getKey(details) {
    var titleEl = details.querySelector('.hdt-facet-title_group span');
    return titleEl ? titleEl.textContent.trim() : null;
  }
  function applyState(details, collapsed) {
    if (collapsed) details.classList.add('cmm-collapsed');
    else details.classList.remove('cmm-collapsed');
  }
  function hydrate() {
    var state = getState();
    document.querySelectorAll('.hdt-control-filter details.hdt-filter-group').forEach(function (det) {
      var key = getKey(det);
      if (key && state[key]) applyState(det, true);
    });
  }

  // Capture-phase click handler so it runs before <hdt-accordion> can interfere
  document.addEventListener('click', function (e) {
    var summary = e.target.closest && e.target.closest('.hdt-control-filter details.hdt-filter-group > summary');
    if (!summary) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var details = summary.parentElement;
    var willCollapse = !details.classList.contains('cmm-collapsed');
    applyState(details, willCollapse);
    var key = getKey(details);
    if (key) {
      var state = getState();
      if (willCollapse) state[key] = true;
      else delete state[key];
      setState(state);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate);
  } else {
    hydrate();
  }
  // Re-apply after filter form re-renders (e.g. after a filter change)
  var obs = new MutationObserver(function () { hydrate(); });
  obs.observe(document.body, { childList: true, subtree: true });
})();
